import { alpha, type Components, type Theme } from '@mui/material/styles';
import { radii } from './shape';

// Global component behavior. Repeated design decisions live here (not in sx),
// per the project AI rules. Component-local layout still uses sx.
export const components: Components<Omit<Theme, 'components'>> = {
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      // Figma DS <Button> (80696:118540, every size/variant): borderRadius-8.
      // This said 4 until it was read off the symbol instead of guessed.
      root: { textTransform: 'none', borderRadius: radii.control, fontWeight: 500 },
    },
  },
  MuiPaper: {
    defaultProps: { elevation: 0 },
    styleOverrides: { root: { backgroundImage: 'none' } },
  },
  // Figma DS <TextField> (79942:41405, Outlined/Small): borderRadius-8 — which is
  // also MUI's own default, so this override only exists to be explicit and to
  // stop the next person "correcting" it back to 4. Outlined = all corners;
  // filled = top corners only (MUI shape). Three screens were patching
  // `.MuiOutlinedInput-root` back to 8 by hand before this was fixed.
  MuiOutlinedInput: {
    styleOverrides: {
      root: { borderRadius: radii.control },
      // Figma DS token `_components/input/outlined/enabledBorder`: GREY at 23%,
      // not MUI's default BLACK at 23%. Hover/focus/error are left to MUI —
      // the DS has no token for them that we've read.
      notchedOutline: { borderColor: 'rgba(128,128,128,0.23)' },
    },
  },
  MuiFilledInput: {
    styleOverrides: {
      root: { borderTopLeftRadius: radii.control, borderTopRightRadius: radii.control },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: radii.control,
        paddingTop: theme.spacing(0.75),
        paddingBottom: theme.spacing(0.75),
        // Figma DS <ListItem>, selected: `primary/_states/selected`
        // (rgba(0,114,255,0.08)) behind PRIMARY-coloured label AND icon — not
        // MUI's neutral `action.selected` grey with the label left dark. One
        // override so every selected row in the left nav reads the same: the nav
        // items, the chat rows, and anything added later.
        // `color: inherit` on the icon is what carries the blue into a lucide
        // glyph, which paints with `currentColor`.
        '&.Mui-selected': {
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          color: theme.palette.primary.main,
          '& .MuiTypography-root': { color: 'inherit' },
          '& svg': { color: 'inherit' },
          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.12) },
        },
      }),
    },
  },
  // radius 4. NOT verified against a DS symbol — pinned to the value it already
  // had so this correction stays limited to what was actually measured.
  MuiAlert: {
    styleOverrides: {
      root: { borderRadius: radii.sm },
    },
  },
  // Figma DS: title is subtitle1 weight 400, not MUI's default 500.
  MuiAlertTitle: {
    styleOverrides: {
      root: { fontWeight: 400 },
    },
  },
  // Figma DS: paper radius 12 (card), not the theme's default 8. Shadow is
  // MUI's elevation-24 — set explicitly so the global MuiPaper elevation:0 default
  // can't flatten it. Everything else (layout/padding/colors) = MUI default = DS.
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: radii.card,
        boxShadow:
          '0px 11px 15px -7px rgba(0,0,0,0.2), 0px 24px 38px 3px rgba(0,0,0,0.14), 0px 9px 46px 8px rgba(0,0,0,0.12)',
      },
    },
  },
  // Figma DS <Chip> (80437:141187): rounded rect on the 4px step, not MUI's
  // default pill; label is body2 (14px) at both sizes, not MUI's 13/12px.
  // Colors/states = MUI default = DS.
  MuiChip: {
    styleOverrides: {
      // 4px radius / 14px label. A default FILLED chip takes the DS grey
      // (palette.surface.chipFill) instead of MUI's #e0e0e0.
      root: ({ ownerState, theme }) => ({
        borderRadius: radii.sm,
        fontSize: '0.875rem',
        paddingBottom: 1,
        ...(ownerState.variant === 'filled' &&
          ownerState.color === 'default' && { backgroundColor: theme.palette.surface.chipFill }),
      }),
    },
  },
  // Figma DS (79212:6021): the overflow menu's paper carries paper/elevation-8
  // (0 0 20 +2 spread, #0000001F). Explicit because the global MuiPaper
  // elevation:0 above would otherwise flatten it. Radius 8 = theme base.
  MuiMenu: {
    styleOverrides: {
      paper: { boxShadow: '0 0 20px 2px rgba(0,0,0,0.12)' },
    },
  },
  // Figma DS <MenuItem> (79212:6021): 40px rows with an 8px icon→label gap.
  MuiMenuItem: {
    styleOverrides: {
      // minHeight is repeated at the `sm` breakpoint because MUI's base style
      // resets it to `auto` there — without this the rows silently collapse to
      // ~36px. `gap` + ListItemIcon minWidth:0 kills MUI's 36px icon gutter so
      // the icon sits tight to the label (was a ~20px gap that looked broken).
      root: ({ theme }) => ({
        minHeight: 40,
        gap: theme.spacing(1),
        [theme.breakpoints.up('sm')]: { minHeight: 40 },
        '& .MuiListItemIcon-root': { minWidth: 0 },
      }),
    },
  },
  // Figma DS: light bubble (#eeedec) w/ secondary text, not MUI's dark default.
  // System-wide contract: every tooltip shows ABOVE its target (placement 'top')
  // and carries the little arrow — set as defaults here so no component repeats it.
  MuiTooltip: {
    defaultProps: { arrow: true, placement: 'top' },
    styleOverrides: {
      tooltip: ({ theme }) => ({
        backgroundColor: theme.palette.surface.tooltip,
        color: theme.palette.text.secondary,
        fontSize: '0.625rem', // 10px
        fontWeight: 500,
        lineHeight: '14px',
        padding: '4px 8px',
        borderRadius: radii.sm,
        boxShadow: '0 0 4px rgba(0,0,0,0.2)', // paper/elevation-2
      }),
      arrow: ({ theme }) => ({ color: theme.palette.surface.tooltip }),
    },
  },
};
